import React, { useState, useEffect, useMemo } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { fetchComplianceStatInfo } from "../../../services/dashboardModule/complianceStatsService.js";
import {
    COMPLIANCE_STATS_ERRORS,
    COMPLIANCE_STATS_MESSAGES,
    COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS,
    formatDate,
} from "../../../common/constants/dashboardModule/complianceStatsConstants.js";

// ── Constants ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";
const GREEN = "#1D9E75";
const PINK  = "#D4537E";

// ── Column config ──────────────────────────────────────────────────
const COLS = [
    { key: COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.CONVERSATION_ID,        label: "Conversation ID",   width: 200, align: "left"   },
    { key: COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.COMPLIANT,              label: "Compliant",         width: 100, align: "center" },
    { key: COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.COMMENTS,               label: "Comments",          width: 320, align: "left"   },
    { key: COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.LAST_CONVERSATION_DATE, label: "Last Conversation", width: 170, align: "left"   },
    { key: COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.LAST_COMPLIANCE_CHECK,  label: "Last Check",        width: 170, align: "left"   },
];

const GRID_COLS     = COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH   = COLS.reduce((s, c) => s + c.width, 0) + 64;
const ROWS_PER_PAGE = 25;

// ── Helpers ────────────────────────────────────────────────────────
function SectionTitle({ children }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
        }}>
            {children}
        </Typography>
    );
}

function CompliantBadge({ value }) {
    const isYes = String(value).toLowerCase() === "yes";
    return (
        <Box component="span" sx={{
            display: "inline-block",
            px: "8px", py: "2px",
            borderRadius: "20px",
            fontSize: 11, fontWeight: 500,
            bgcolor: isYes ? "rgba(29,158,117,0.1)" : "rgba(212,83,126,0.1)",
            color:   isYes ? GREEN : PINK,
            border:  `0.5px solid ${isYes ? "rgba(29,158,117,0.3)" : "rgba(212,83,126,0.3)"}`,
        }}>
            {value}
        </Box>
    );
}

// ── Component ──────────────────────────────────────────────────────
const ComplianceStatInfo = ({ complianceRuleID, complianceType, onBack }) => {
    const [data,    setData]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState("");
    const [page,    setPage]    = useState(0);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState("asc");

    useEffect(() => { load(); }, [complianceType, complianceRuleID]);

    const load = async () => {
        try {
            setLoading(true); setError(""); setPage(0);
            setData(await fetchComplianceStatInfo(complianceType, complianceRuleID));
        } catch (e) {
            console.error(COMPLIANCE_STATS_ERRORS.FETCH_COMPLIANCE_STATS_ERROR, e);
            setError("Failed to load compliance detail.");
        } finally {
            setLoading(false);
        }
    };

    const handleSortCol = (key) => {
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortKey) return data;
        return [...data].sort((a, b) => {
            const cmp = String(a[sortKey] ?? "").localeCompare(String(b[sortKey] ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [data, sortKey, sortDir]);

    const pagedData  = useMemo(() =>
        sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sortedData, page]);

    const totalPages = Math.ceil(data.length / ROWS_PER_PAGE);

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Header ── */}
            <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <Box
                    onClick={() => { onBack(); setData([]); }}
                    sx={{
                        display: "flex", alignItems: "center", gap: 0.5,
                        px: "12px", py: "5px", borderRadius: "20px",
                        border: "0.5px solid", borderColor: "divider",
                        cursor: "pointer", userSelect: "none",
                        fontSize: 13, color: "text.secondary",
                        "&:hover": { borderColor: BLUE, color: BLUE },
                    }}
                >
                    <ArrowBackRoundedIcon sx={{ fontSize: 14 }} />
                    Back
                </Box>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Compliance Stat —&nbsp;
                    <Box component="span" sx={{ color: BLUE, fontWeight: 500 }}>
                        Rule {complianceRuleID}
                    </Box>
                </Typography>
            </Box>

            {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                    <CircularProgress size={20} /> Loading…
                </Box>
            )}

            {!loading && data.length === 0 && !error && (
                <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                    {COMPLIANCE_STATS_MESSAGES.NO_COMPLIANCE_STAT_RULE}
                </Typography>
            )}

            {!loading && data.length > 0 && (
                <>
                    <SectionTitle>Detail</SectionTitle>

                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                        {/* Header row */}
                        <Box sx={{
                            ...gridSx, py: 1,
                            background: "#f7f7f7",
                            borderBottom: "0.5px solid", borderColor: "divider",
                            fontSize: 11, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.05em",
                        }}>
                            {COLS.map(col => (
                                <Box
                                    key={col.key}
                                    onClick={() => handleSortCol(col.key)}
                                    sx={{
                                        textAlign: col.align, cursor: "pointer", userSelect: "none",
                                        display: "flex", gap: 0.5, alignItems: "center",
                                        justifyContent:
                                            col.align === "right"  ? "flex-end"  :
                                            col.align === "center" ? "center"    : "flex-start",
                                        color: sortKey === col.key ? BLUE : "text.secondary",
                                        "&:hover": { color: BLUE },
                                    }}
                                >
                                    {col.label}
                                    {sortKey === col.key && (
                                        <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        {/* Data rows */}
                        {pagedData.map((row, i) => (
                            <Box key={row.ConversationID ?? i} sx={{
                                ...gridSx, py: "7px", fontSize: 12,
                                borderBottom: "0.5px solid", borderColor: "divider",
                                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                                "&:hover": { background: "rgba(55,138,221,0.06)" },
                            }}>
                                {COLS.map(col => (
                                    <Box key={col.key} sx={{
                                        display: "flex", alignItems: "center",
                                        justifyContent:
                                            col.align === "center" ? "center" :
                                            col.align === "right"  ? "flex-end" : "flex-start",
                                        ...(col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.CONVERSATION_ID && {
                                            fontFamily: "monospace", color: "text.secondary", fontSize: 11,
                                        }),
                                        ...(col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.COMMENTS && {
                                            overflow: "hidden", textOverflow: "ellipsis",
                                            whiteSpace: "nowrap", pr: 2, color: "text.primary",
                                        }),
                                        ...((col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.LAST_CONVERSATION_DATE ||
                                             col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.LAST_COMPLIANCE_CHECK) && {
                                            color: "text.secondary",
                                        }),
                                    }}>
                                        {col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.COMPLIANT        ? <CompliantBadge value={row.Compliant} />
                                       : col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.LAST_CONVERSATION_DATE ? formatDate(row.LastConversationDate)
                                       : col.key === COMPLIANCE_STATS_INFO_TABLE_COLUMN_KEYS.LAST_COMPLIANCE_CHECK  ? formatDate(row.LastComplianceCheck)
                                       : (row[col.key] ?? "—")}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <Box sx={{
                            display: "flex", justifyContent: "space-between",
                            alignItems: "center", mt: 1, px: 1,
                        }}>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, data.length)} of {data.length}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                                <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page === 0 ? "default" : "pointer",
                                    opacity: page === 0 ? 0.4 : 1,
                                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                                }}>‹</Box>
                                <span>Page {page + 1} of {totalPages}</span>
                                <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                                    px: "10px", py: "3px", borderRadius: "20px",
                                    border: "0.5px solid", borderColor: "divider",
                                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                                }}>›</Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ComplianceStatInfo;